import { useDoctorsStore } from './stores/doctors';
import { usePatientsStore } from './stores/patients';
import { useServicesStore } from './stores/services';
import { usePrescriptionsStore } from './stores/prescriptions';
import { useAnalyticsStore } from './stores/analytics';
import { useMasterPatientsStore } from './stores/masterPatients';
import { useConsultationsStore } from './stores/consultations';
import { useAuthStore } from '@/lib/auth';
import { PatientStatus, Service } from '@/types';
import { create } from 'zustand';
import { useBillsStore } from './stores/bills';

// Re-export individual store hooks
export {
  useDoctorsStore,
  usePatientsStore,
  useServicesStore,
  usePrescriptionsStore,
  useAnalyticsStore,
  useMasterPatientsStore,
  useConsultationsStore
};

// Create a combined store for backward compatibility
export const useQueueStore = () => {
  const patientsStore = usePatientsStore.getState();
  const doctorsStore = useDoctorsStore.getState();
  const servicesStore = useServicesStore.getState();
  const prescriptionsStore = usePrescriptionsStore.getState();
  const analyticsStore = useAnalyticsStore.getState();
  const masterPatientsStore = useMasterPatientsStore.getState();
  const consultationsStore = useConsultationsStore.getState();
  const authStore = useAuthStore.getState();
  const billsStore = useBillsStore.getState();
  
  return {
    // Doctors
    doctors: doctorsStore.doctors,
    addDoctor: doctorsStore.addDoctor,
    updateDoctor: doctorsStore.updateDoctor,
    fetchDoctors: doctorsStore.fetchDoctors,
    
    // Patients
    patients: patientsStore.patients,
    lastUpdated: patientsStore.lastUpdated,
    addPatient: patientsStore.addPatient,
    updatePatient: patientsStore.updatePatient,
    fetchPatients: patientsStore.fetchPatients,
    updatePatientStatus: (patientId: string, status: PatientStatus, doctorId?: string) => {
      try {
        // If doctorId is provided, use it directly
        // Otherwise, try to get it from the current user
        const assignedDoctorId = doctorId || useAuthStore.getState().user?.id;
        
        if (!assignedDoctorId) {
          throw new Error('No doctor ID found');
        }
        
        patientsStore.updatePatientStatus(patientId, status, assignedDoctorId);
      } catch (error) {
        console.error('Error updating patient status:', error);
        throw error;
      }
    },
    updatePatientDoctor: patientsStore.updatePatientDoctor,
    addPatientServices: patientsStore.addPatientServices,
    updatePatientPayment: patientsStore.updatePatientPayment,
    updatePatientFollowUp: patientsStore.updatePatientFollowUp,
    resetPatientSession: patientsStore.resetPatientSession,
    removePatient: patientsStore.removePatient,
    getPatientsByStatus: patientsStore.getPatientsByStatus,
    getPatientsByDoctor: patientsStore.getPatientsByDoctor,
    
    // Master Patients (New)
    masterPatients: masterPatientsStore.masterPatients,
    fetchMasterPatients: masterPatientsStore.fetchMasterPatients,
    getMasterPatient: masterPatientsStore.getMasterPatient,
    getMasterPatientByContact: masterPatientsStore.getMasterPatientByContact,
    createMasterPatient: masterPatientsStore.createMasterPatient,
    updateMasterPatient: masterPatientsStore.updateMasterPatient,
    deleteMasterPatient: masterPatientsStore.deleteMasterPatient,
    searchMasterPatients: masterPatientsStore.searchMasterPatients,
    findOrCreateMasterPatient: masterPatientsStore.findOrCreateMasterPatient,
    
    // Consultations (New)
    consultations: consultationsStore.consultations,
    fetchConsultations: consultationsStore.fetchConsultations,
    getConsultation: consultationsStore.getConsultation,
    getConsultationsByMasterPatient: consultationsStore.getConsultationsByMasterPatient,
    getConsultationsByPatient: consultationsStore.getConsultationsByPatient,
    createConsultation: consultationsStore.createConsultation,
    updateConsultation: consultationsStore.updateConsultation,
    deleteConsultation: consultationsStore.deleteConsultation,
    linkPatientToMaster: consultationsStore.linkPatientToMaster,
    
    // Services
    services: servicesStore.services,
    addService: servicesStore.addService,
    updateService: servicesStore.updateService,
    removeService: servicesStore.removeService,
    getServices: servicesStore.getServices,
    
    // Prescriptions
    prescriptions: prescriptionsStore.prescriptions,
    addPrescription: prescriptionsStore.addPrescription,
    updatePrescription: prescriptionsStore.updatePrescription,
    removePrescription: prescriptionsStore.removePrescription,
    getPrescriptionsByPatient: prescriptionsStore.getPrescriptionsByPatient,
    getPrescriptionsByDoctor: prescriptionsStore.getPrescriptionsByDoctor,
    fetchPrescriptions: prescriptionsStore.fetchPrescriptions,
    
    // Analytics
    getRevenueSummary: analyticsStore.getRevenueSummary,
    getPatientCountByDoctor: analyticsStore.getPatientCountByDoctor,
    getCompletedAppointmentsByDate: analyticsStore.getCompletedAppointmentsByDate,
    
    // Bills
    bills: billsStore.bills,
    fetchBills: billsStore.fetchBills,
  };
};
